import { useEffect, useMemo, useState } from 'react';
import OlVectorLayer from 'ol/layer/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { useOpenLayers } from '../react-ol';
import useClusterSource from '../react-ol/hooks/useClusterSource';
import Point from 'ol/geom/Point';

/**
 * Provides a vector layer and source to display clustered points
 *
 * Each feature needs to have unique id
 *
 * Clusters style, text, distance config are customisable
 *
 * Can select single or multiple clusters
 */
var ClusterGeometry = _ref => {
  var {
    data,
    id,
    label,
    maxZoom,
    minZoom,
    zIndex,
    clusterDistance,
    onSelect,
    minDistance,
    multiSelect,
    displaySize,
    canSelect = false,
    selectedStyles,
    styles,
    reference,
    getFeatureIds
  } = _ref;
  var [vectorLayer, setVectorLayer] = useState();
  var {
    map,
    layerRef
  } = useOpenLayers();
  var stylesMemoized = useMemo(() => {
    return styles;
  }, [styles]);
  var selectedStylesMemoized = useMemo(() => {
    return selectedStyles;
  }, [selectedStyles]);
  useEffect(() => {
    var vectorLayerConfig = {
      visible: true,
      zIndex: zIndex !== null && zIndex !== void 0 ? zIndex : 10,
      minZoom,
      maxZoom,
      // assigning custom layer props
      properties: {
        reference
      }
    };
    var newVectorLayer = new OlVectorLayer(vectorLayerConfig);
    setVectorLayer(newVectorLayer);
    return () => {
      newVectorLayer.dispose();
    };
  }, [maxZoom, minZoom, reference, zIndex]);
  useEffect(() => {
    if (vectorLayer) {
      vectorLayer.set('id', id);
      vectorLayer.set('label', label);
    }
  }, [id, label, vectorLayer]);
  useEffect(() => {
    if (!map || !vectorLayer) return;

    if (layerRef) {
      // set vectorLayer ref in context
      layerRef.current = vectorLayer;
    } // add vector layer to map


    map.addLayer(vectorLayer); // on component unmount remove current vector layer from map and dispose the layer

    return () => {
      vectorLayer.dispose();
      map.removeLayer(vectorLayer);
    };
  }, [layerRef, map, vectorLayer]);
  /**
   * Read geojson and return features
   */

  var features = useMemo(() => {
    if (!data) return;

    try {
      var parsedFeatures = new GeoJSON().readFeatures(data).map(feat => feat); // If geometry type is not Point we don't render the shapes and throw an error

      var isValidGeometry = parsedFeatures.every(f => f.getGeometry() instanceof Point);

      if (!isValidGeometry) {
        throw new Error('Please provide Point geometry type');
      }

      return parsedFeatures;
    } catch (e) {
      console.error(e);
      return;
    }
  }, [data]);

  var handleOnSelect = ids => {
    getFeatureIds === null || getFeatureIds === void 0 ? void 0 : getFeatureIds(ids);
    onSelect === null || onSelect === void 0 ? void 0 : onSelect(ids);
  };

  useClusterSource({
    features,
    clusterDistance,
    minDistance,
    onSelect: handleOnSelect,
    multiSelect,
    displaySize,
    vectorLayer,
    canSelect,
    layerId: id,
    styles: stylesMemoized,
    selectedStyles: selectedStylesMemoized
  });
  return null;
};

export default ClusterGeometry;